import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminGuard from "@/components/admin/AdminGuard";
import StatsCard from "@/components/admin/StatsCard";
import { useApp } from "@/lib/AppContext";

export default function AdminDashboard() {
  const { lang } = useApp();
  const isAr = lang === "ar";
  const tr = {
    ar: {
      title: "لوحة التحكم الرئيسية", subtitle: "نظرة عامة على جميع الأنشطة",
      refresh: "تحديث",
      laundryTitle: "المغسلة الإلكترونية", laundrySub: "إحصائيات الطلبات والإيرادات",
      todayOrders: "طلبات اليوم", pendingOrders: "طلبات جديدة", activeOrders: "طلبات نشطة", deliveredOrders: "مُسلَّمة",
      revenue: "إجمالي الإيرادات", unit: "ر.س", pendingInvoices: "فواتير معلقة", activeDelegates: "مندوبون نشطون", totalOrders: "إجمالي الطلبات",
      wheelTitle: "عجلة العروض", wheelSub: "إحصائيات العروض والكوبونات",
      totalSpins: "إجمالي اللفات", totalCustomers: "العملاء المشاركون", totalCoupons: "الكوبونات الصادرة", usageRate: "نسبة الاستخدام",
      usedCoupons: "كوبونات مستخدمة", unusedCoupons: "كوبونات غير مستخدمة",
      todayLabel: "اليوم", last7Label: "آخر 7 أيام",
    },
    en: {
      title: "Main Dashboard", subtitle: "Overview of all activities",
      refresh: "Refresh",
      laundryTitle: "E-Laundry", laundrySub: "Orders & revenue stats",
      todayOrders: "Today's Orders", pendingOrders: "New Orders", activeOrders: "Active Orders", deliveredOrders: "Delivered",
      revenue: "Total Revenue", unit: "SAR", pendingInvoices: "Pending Invoices", activeDelegates: "Active Delegates", totalOrders: "Total Orders",
      wheelTitle: "Offers Wheel", wheelSub: "Offers & coupon stats",
      totalSpins: "Total Spins", totalCustomers: "Customers", totalCoupons: "Coupons Issued", usageRate: "Usage Rate",
      usedCoupons: "Used Coupons", unusedCoupons: "Unused Coupons",
      todayLabel: "Today", last7Label: "Last 7 Days",
    }
  }[lang];

  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey(k => k + 1);

  const { data: dashboardData, isLoading } = useQuery({
    queryKey: ['dashboard-stats', refreshKey],
    queryFn: async () => {
      const [spins, coupons, customers, orders, delegates] = await Promise.all([
        base44.entities.Spin.list("-created_date", 1000),
        base44.entities.Coupon.list("-created_date", 1000),
        base44.entities.Customer.list("-created_date", 500),
        base44.entities.LaundryOrder.list("-created_date", 500),
        base44.entities.Delegate.filter({ is_active: true }),
      ]);
      return { spins, coupons, customers, orders, delegates };
    },
    staleTime: 30000, // 30 seconds cache
    retry: 2,
  });

  // Calculate stats from cached data
  const wheelStats = (() => {
    if (!dashboardData) return null;
    const { spins, coupons, customers } = dashboardData;
    const used = coupons.filter(c => c.status === "used");
    const unused = coupons.filter(c => c.status === "unused");
    const usageRate = coupons.length > 0 ? Math.round((used.length / coupons.length) * 100) : 0;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const last7 = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000);
    return {
      totalSpins: spins.length,
      totalCustomers: customers.length,
      totalCoupons: coupons.length,
      usedCoupons: used.length,
      unusedCoupons: unused.length,
      usageRate,
      todaySpins: spins.filter(s => new Date(s.created_date) >= today).length,
      last7Spins: spins.filter(s => new Date(s.created_date) >= last7).length,
    };
  })();

  const laundryStats = (() => {
    if (!dashboardData) return null;
    const { orders, delegates } = dashboardData;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return {
      totalOrders: orders.length,
      pendingOrders: orders.filter(o => o.status === "pending").length,
      activeOrders: orders.filter(o => !["delivered", "cancelled"].includes(o.status)).length,
      deliveredOrders: orders.filter(o => o.status === "delivered").length,
      todayOrders: orders.filter(o => new Date(o.created_date) >= today).length,
      totalRevenue: orders.filter(o => o.paid).reduce((s, o) => s + (o.total_amount || 0), 0),
      pendingInvoices: orders.filter(o => o.total_amount > 0 && !o.invoice_sent && !["delivered", "cancelled"].includes(o.status)).length,
      activeDelegates: delegates.length,
    };
  })();

  if (isLoading || !wheelStats || !laundryStats) {
    return (
      <AdminGuard>
        <AdminLayout>
          <div className="flex items-center justify-center h-64">
            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        </AdminLayout>
      </AdminGuard>
    );
  }

  return (
    <AdminGuard>
      <AdminLayout>
        <div className="space-y-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-black text-foreground">{tr.title}</h1>
              <p className="text-muted-foreground text-sm">{tr.subtitle}</p>
            </div>
            <button onClick={refresh} className="px-4 py-2 bg-secondary rounded-xl text-sm font-medium hover:bg-secondary/80 transition-colors text-foreground">
              🔄 {tr.refresh}
            </button>
          </div>

          {/* ── قسم المغسلة الإلكترونية ── */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center text-white text-lg">🧺</div>
              <div>
                <h2 className="text-lg font-black text-foreground">{tr.laundryTitle}</h2>
                <p className="text-xs text-muted-foreground">{tr.laundrySub}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <StatsCard icon="📥" label={tr.todayOrders} value={laundryStats.todayOrders} color="primary" />
              <StatsCard icon="⏳" label={tr.pendingOrders} value={laundryStats.pendingOrders} color="amber" />
              <StatsCard icon="🔄" label={tr.activeOrders} value={laundryStats.activeOrders} color="accent" />
              <StatsCard icon="✅" label={tr.deliveredOrders} value={laundryStats.deliveredOrders} color="green" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <StatsCard icon="💰" label={tr.revenue} value={`${laundryStats.totalRevenue} ${tr.unit}`} color="gold" />
              <StatsCard icon="📧" label={tr.pendingInvoices} value={laundryStats.pendingInvoices} color="red" />
              <StatsCard icon="👤" label={tr.activeDelegates} value={laundryStats.activeDelegates} color="primary" />
              <StatsCard icon="📦" label={tr.totalOrders} value={laundryStats.totalOrders} color="accent" />
            </div>
          </section>

          {/* Divider */}
          <div className="border-t border-border" />

          {/* ── قسم عجلة العروض ── */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white text-lg">🎡</div>
              <div>
                <h2 className="text-lg font-black text-foreground">{tr.wheelTitle}</h2>
                <p className="text-xs text-muted-foreground">{tr.wheelSub}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <StatsCard icon="🎡" label={tr.totalSpins} value={wheelStats.totalSpins} color="primary" />
              <StatsCard icon="👥" label={tr.totalCustomers} value={wheelStats.totalCustomers} color="accent" />
              <StatsCard icon="🎫" label={tr.totalCoupons} value={wheelStats.totalCoupons} color="gold" />
              <StatsCard icon="📊" label={tr.usageRate} value={`${wheelStats.usageRate}%`} color="green" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <StatsCard icon="✅" label={tr.usedCoupons} value={wheelStats.usedCoupons} color="green" />
              <StatsCard icon="⏳" label={tr.unusedCoupons} value={wheelStats.unusedCoupons} color="amber" />
              <StatsCard icon="🌟" label={tr.todayLabel} value={wheelStats.todaySpins} color="primary" />
              <StatsCard icon="📅" label={tr.last7Label} value={wheelStats.last7Spins} color="accent" />
            </div>
          </section>
        </div>
      </AdminLayout>
    </AdminGuard>
  );
}